import { Component } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import {
    AuthenticationService,
    forLifeOf,
    LifecycleAware,
    MFAType,
    focusOnFirstInvalidLsqInput,
    LsqValidators
} from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { LoginPageStep, LoginService } from '../login.service';

@LifecycleAware()
@Component({
    selector: 'mfa-code',
    styleUrls: ['./login-step.scss'],
    template: `
        <h1 i18n>Multi-Factor Authentication</h1>
        <ng-container *ngIf="mfaType !== MFAType.TOTP">
            <form class="login-step" [formGroup]="formGroup" (ngSubmit)="submitCode()">
                <p class="body" [style.marginBottom]="'2rem'" i18n>
                    To continue, please enter the 6-digit verification code sent to your mobile
                    phone number ending in {{ phoneNumberLastFour$ | async }}.
                </p>

                <!-- TODO(PDEV-223): Remove label in favor of input's own label -->
                <p class="label" [style.marginBottom]="'-0.5rem'" i18n>Enter SMS Code</p>
                <lsq-input
                    [style.marginBottom]="'1rem'"
                    name="mfaCode"
                    [basicLabel]="false"
                    formControlName="mfaCode"
                    [group]="formGroup"
                    [maxLength]="6"
                    [autoFocus]="true"
                >
                </lsq-input>

                <p [style.marginBottom]="'2rem'" i18n>
                    Didn't receive a code?

                    <lsq-button
                        appearance="transparent"
                        (buttonClick)="resendCode()"
                        [style.marginLeft]="'1rem'"
                        [style.display]="'inline-block'"
                    >
                        Resend
                    </lsq-button>
                </p>

                <lsq-button
                    type="submit"
                    size="block"
                    [submitting]="isSubmitting"
                    [style.marginBottom]="'2rem'"
                    i18n
                >
                    Continue
                </lsq-button>

                <lsq-button
                    appearance="transparent"
                    size="block"
                    (buttonClick)="handleDoesNotHaveAccess()"
                    i18n
                >
                    I don’t have access to this phone
                </lsq-button>
            </form>
        </ng-container>

        <ng-container *ngIf="mfaType == MFAType.TOTP">
            <form class="login-step" [formGroup]="totpGroup" (ngSubmit)="submitTOTPCode()">
                <p class="body" [style.marginBottom]="'2rem'" i18n>
                    To continue, please enter the verification code provided by your Duo Mobile
                    authenticator app.
                </p>

                <p class="label" [style.marginBottom]="'-0.5rem'" i18n>Enter Code</p>
                <lsq-input
                    [style.marginBottom]="'1rem'"
                    name="totpCode"
                    [basicLabel]="false"
                    formControlName="totpCode"
                    [group]="totpGroup"
                    [maxLength]="6"
                    [autoFocus]="true"
                >
                </lsq-input>

                <lsq-button
                    type="submit"
                    size="block"
                    [submitting]="isSubmitting"
                    [style.marginBottom]="'2rem'"
                    i18n
                >
                    Continue
                </lsq-button>

                <lsq-button
                    appearance="transparent"
                    size="block"
                    (buttonClick)="handleDoesNotHaveAccess()"
                    i18n
                >
                    I don’t have access to this device
                </lsq-button>
            </form>
        </ng-container>
    `
})
export class MfaCodeComponent {
    public phoneNumberLastFour$ = this._loginService.phoneNumberLastFour$;
    public isSubmitting = false;
    private _mfaCodeFailure = false;
    public MFAType = MFAType;
    public mfaType: MFAType;
    public mfaType$ = this._loginService.mfaType$;

    private _invalidAuthCodeCopy = this._i18n({
        value: 'Invalid Code'
    });

    public formGroup = this._formBuilder.group({
        mfaCode: [
            '',
            [
                Validators.required,
                LsqValidators.number,
                LsqValidators.exactLength(6),
                () =>
                    this._mfaCodeFailure
                        ? { mfaCodeInvalid: { message: this._invalidAuthCodeCopy } }
                        : null
            ]
        ]
    });

    public totpGroup = this._formBuilder.group({
        totpCode: [
            '',
            [
                Validators.required,
                LsqValidators.number,
                LsqValidators.exactLength(6),
                // This lets us show a custom error message on the input.
                () =>
                    this._mfaCodeFailure
                        ? { mfaCodeInvalid: { message: this._invalidAuthCodeCopy } }
                        : null
            ]
        ]
    });

    constructor(
        private _i18n: I18n,
        private _formBuilder: FormBuilder,
        private _loginService: LoginService,
        private _authenticationService: AuthenticationService
    ) {
        this.mfaType$.subscribe((val) => (this.mfaType = val));
        this.formGroup.valueChanges.pipe(forLifeOf(this)).subscribe(() => {
            this._mfaCodeFailure = false;
        });
        this.totpGroup.valueChanges.pipe(forLifeOf(this)).subscribe(() => {
            this._mfaCodeFailure = false;
        });
    }

    public async submitCode(): Promise<void> {
        this._mfaCodeFailure = false;
        this.formGroup.markAllAsTouched();
        this.formGroup.get('mfaCode').updateValueAndValidity();

        if (this.formGroup.valid) {
            this.isSubmitting = true;

            const mfaCode = this.formGroup.get('mfaCode').value;

            const result = await this._authenticationService.validateAuthenticationCode(
                mfaCode,
                this.mfaType
            );

            if (result.authorized) {
                await this._loginService.handleSuccessfulMfa(result.accessToken);
            } else {
                this._mfaCodeFailure = true;
                this.formGroup.get('mfaCode').updateValueAndValidity();
            }
        }
        this.isSubmitting = false;
    }

    public async resendCode(): Promise<void> {
        this.formGroup.reset();

        await this._loginService.resendMfaCode();
    }

    public async handleDoesNotHaveAccess(): Promise<void> {
        this._loginService.setLoginPageStep(LoginPageStep.BackupCode);
    }

    public async submitTOTPCode(): Promise<void> {
        this._mfaCodeFailure = false;
        this.totpGroup.markAllAsTouched();
        this.totpGroup.get('totpCode').updateValueAndValidity();

        if (this.totpGroup.valid) {
            this.isSubmitting = true;

            const totpCode = this.totpGroup.get('totpCode').value;

            const result = await this._authenticationService.validateAuthenticationCode(
                totpCode,
                this.mfaType
            );

            if (result.authorized) {
                await this._loginService.handleSuccessfulMfa(result.accessToken);
            } else {
                this._mfaCodeFailure = true;
                this.totpGroup.get('totpCode').updateValueAndValidity();
                focusOnFirstInvalidLsqInput();
            }
        }
        this.isSubmitting = false;
    }
}
